import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  deleteDeadLetters,
  getDeadLetters,
  replayDeadLetters,
} from "#/modules/admin.queue/api.dead-letters-admin.server";
import { adminMiddleware } from "#/modules/util.auth/middleware.admin";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { m } from "#/paraglide/messages";

// Mirrors GetDeadLettersHandler.PageSize on the backend.
const PAGE_SIZE = 50;

/** GET /admin/dead-letters — paginated view of the Wolverine dead letter queue. */
export const getAdminDeadLetters = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .validator(z.object({ page: z.number().int().min(1).default(1) }))
  .handler(async ({ data }) => {
    const result = await getDeadLetters(data.page);
    if (Result.isError(result)) throw result.error;

    const totalCount = result.value?.totalCount ?? 0;
    return {
      deadLetters: result.value?.deadLetters ?? [],
      totalCount,
      totalPages: Math.ceil(totalCount / PAGE_SIZE),
    };
  });

/** POST /admin/dead-letters/replay — mark envelopes replayable; the durability agent re-delivers. */
export const replayAdminDeadLetters = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ ids: z.array(z.string().min(1)).min(1) }))
  .handler(async ({ data }) => {
    const result = await replayDeadLetters(data.ids);
    if (Result.isError(result)) {
      throw new Error(m["common.something_went_wrong"]());
    }
  });

/** DELETE /admin/dead-letters — permanently remove envelopes from the dead letter queue. */
export const deleteAdminDeadLetters = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ ids: z.array(z.string().min(1)).min(1) }))
  .handler(async ({ data }) => {
    const result = await deleteDeadLetters(data.ids);
    if (Result.isError(result)) {
      throw new Error(m["common.something_went_wrong"]());
    }
  });
